import styled from "@emotion/styled/macro";
import React, { useState } from "react";
import ReactGA from "react-ga";

import { differenceInYears } from "date-fns";

import DialogPortal from "../common/DialogPortal";
import DialogRoot from "../common/DialogRoot";
import Button from "../common/Button";
import PhoneNumberFields, { IPhoneData } from "../common/PhoneNumberFields";
import ZipCodeInput from "../common/ZipCodeInput";

import ApiService from "../ApiService";
import bobbleheadLogo from "../res/favicon-32x32.png";
import teamLogoRays from "../res/Tampa_Bay_Rays.svg";
import teamLogoMets from "../res/Mets_Word_Mark_Transparent.png";
//import FormHint from "../common/FormHint";
import FormField from "../common/FormField";
import TextInput from "../common/TextInput";
import PlayFab from "../Playfab/PlayFab";
import MainIndex, { bobbleHeadID } from "../index";

const Root = styled(DialogRoot)`
  width: 340px;
  height: 90%;
  position: relative;
  padding: 30px 15px 58px;
  font-size: 15px;
  overflow-y: auto;
`;

const Instructions = styled("p")`
  padding: 0 8px;
`;

const Icon = styled<"div", { logo: string }>("div")`
  width: 80px;
  height: 80px;
  background: url(${({ logo }) => logo}) no-repeat center center #edf0f6;
  background-size: 50px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  position: relative;
  top: 35px;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Error = styled("div")`
  color: #fff;
  background-color: #F5D130;
  text-align: center;
  text-transform: uppercase;
  font-size: 11px;
  font-weight: bold;
  line-height: 2;
  margin-top: 12px;
`;

const SubmitButton = styled(Button)`
  width: 88%;
  position: relative;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%) translateY(50%);
`;

const Terms = styled("label")`
  display: block;
  align-items: center;
  font-size: 15px;
  margin-top: 18px;
  padding: 0 24px;

  input[type="checkbox"] {
    background: #ccd0da;
    border: 1px solid #707381;
    width: 20px;
    height: 20px;
    margin-right: 7px;

    &:checked {
      background-color: #707381;
      border-color: inherit;
    }
  }

  > a {
    color: inherit;
    margin-left: 4px;
    text-decoration: underline;
  }
`;

const Opt = styled("label")`
  display: block;
  align-items: center;
  font-size: 15px;
  margin-top: 18px;
  padding: 0 24px;

  input[type="checkbox"] {
    background: #ccd0da;
    border: 1px solid #707381;
    width: 20px;
    height: 20px;
    margin-right: 7px;

    &:checked {
      background-color: #707381;
      border-color: inherit;
    }
  }

  > a {
    color: inherit;
    margin-left: 4px;
    text-decoration: underline;
  }
`;

const DOB = styled("label")`
  margin-left: 22px;
`;

const DateInput = styled(TextInput)`
  width: 77%;
  margin-left: 23px;
`;

const ZipCodeField = styled(FormField)`
  width: 93%;
  padding: 0 22px;
`;

const ErrorDisplay = styled("div")`
  color: #fff;
  background-color: #cb3d3d;
  text-align: center;
  text-transform: uppercase;
  font-size: 11px;
  font-weight: bold;
  line-height: 2;
  margin-top: 12px;
`;

interface IProps {
  primaryLogoUrl: string | null;
  onClose: () => void;
  onSuccess: (
    response: any
  ) => void;
}

function getValidDate(date: string): Date | null {
  try {
    return new Date(date);
  } catch (e) {
    return null;
  }
}

let logoType: string = "";

export default function SignInDialog({
  primaryLogoUrl,
  onClose,
  onSuccess
}: IProps) {
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [dateOfBirthChanged, setDateOfBirthChanged] = useState(false);
  //const [dateOfBirthBlurred, setDateOfBirthBlurred] = useState(false);
  const [zipCode, setZipCode] = useState("");
  const [zipCodeChanged, setZipCodeChanged] = useState(false);
  //const [zipCodeBlurred, setZipCodeBlurred] = useState(false);
  const [showAgeError, setShowAgeError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const parsedDate = getValidDate(dateOfBirth);
  const dateOfBirthIsValid = !!parsedDate && parsedDate.getFullYear() > 1900;
  const zipCodeIsValid = /^\d{5}$/.test(zipCode);
  const [phoneData, setPhoneData] = useState<IPhoneData>({
    countryCode: "1",
    phoneNumber: null,
    formattedPhoneNumber: ""
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedOptIn, setAcceptOptIn] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const { countryCode, phoneNumber } = phoneData;

  if(MainIndex.GetBobbleID() === "M0") {
    logoType = teamLogoMets;
  } else {
    logoType = teamLogoRays;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptedTerms || !phoneNumber) {
      return;
    }

    if(parsedDate) {
      const age = differenceInYears(new Date(), parsedDate);
      if (age < 18) {
        setShowAgeError(true);
        return;
      }
    }

    const userId = `${phoneNumber}`;
    setIsSending(true);

    ReactGA.event({
      category: "sign up",
      action: "submit login info",
      label: "email"
    });

    try {
      const logInResult = await ApiService.logIn(userId);
      if (!logInResult) {
        setIsSending(false);
      } else {
        try {
          //console.log("setting contact email");
          PlayFab.SetBirth(dateOfBirth);
          PlayFab.SetZip(zipCode);
          PlayFab.SetOptIn(String(acceptedOptIn));
        } catch (e) {
          console.error("Error signing in", e);
        }
        onSuccess(logInResult);
      }
    } catch (e) {
      console.error(e);
      setIsSending(false);
    }
  };

  if(bobbleHeadID === "M0") {
    return (
      <DialogPortal onClose={onClose}>
        <Root hasLogo={!!primaryLogoUrl}>
          <Icon logo={logoType} />
          <form onSubmit={handleSubmit}>
            <Instructions>
              Please enter your email address, birthdate, and ZIP Code to see if you have received an added-value bonus.
            </Instructions>
            <PhoneNumberFields
              disabled={isSending}
              onChange={data => {
                setPhoneData(data);
              }}
            />
            <FormField>
              <DOB>Birthdate*</DOB>
              <DateInput
                type="date"
                placeholder="mm/dd/yyyy"
                value={dateOfBirth}
                center
                min="1900-01-01"
                onChange={e => {
                  setDateOfBirth(e.target.value);
                  PlayFab.SetBirth(e.target.value);
                  setDateOfBirthChanged(true);
                }}
                isValid={
                  !showAgeError &&
                  (!dateOfBirth ||
                    !dateOfBirthChanged ||
                    dateOfBirthIsValid)
                }
              />
            </FormField>
            {showAgeError && <Error>Sorry, you are not eligible to participate</Error>}
            <ZipCodeField>
              <label>Zip Code*</label>
              <ZipCodeInput
                placeholder="12345"
                value={zipCode}
                disabled={isSending}
                onChange={(e: any) => {
                  setZipCode(e.target.rawValue);
                  setZipCodeChanged(true);
                }}
                isValid={(!zipCodeChanged || zipCodeIsValid) && zipCode !== "00000"}
              />
              </ZipCodeField>
            {/*<FormHint>Some Bonuses are only available in certain areas.</FormHint>*/}
            {error && <ErrorDisplay>{error}</ErrorDisplay>}
            {/*<FormHint>
              Your email address will only be used for sign in purposes.{" "}
            </FormHint>*/}
            <Opt htmlFor="acceptOptIn">
              <input
                id="acceptOptIn"
                type="checkbox"
                disabled={isSending}
                checked={acceptedOptIn}
                onChange={e => {
                  setAcceptOptIn(e.target.checked);
                }}
              />{" "}
              I would like to receive commercial emails from{" "}
              <a
                href="https://www.mlb.com/mets"
                target="_blank"
                rel="noopener noreferrer"
              >
                mets.com
              </a>
              ,{" "}
              <a
                href="https://www.mlb.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                mlb.com
              </a>
              , and their partners (optional).
            </Opt>
            <Terms htmlFor="acceptTandC">
              <input
                id="acceptTandC"
                type="checkbox"
                disabled={isSending}
                checked={acceptedTerms}
                onChange={e => {
                  setAcceptedTerms(e.target.checked);
                }}
              />{" "}
              I accept the{""}
              <a
                href="https://www.mlb.com/official-information/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
              >
              Terms of Use 
              </a>
              {""} and {""}
              <a
                href="https://www.mlb.com/official-information/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
              Privacy Policy
              </a>
              *
            </Terms>
            <SubmitButton
              onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
              disabled={
                isSending || !acceptedTerms || !countryCode || !phoneNumber || !zipCodeIsValid
              }
            >
              Unlock My Bonus
            </SubmitButton>
          </form>
        </Root>
      </DialogPortal>
    );
  } else {
    return (
      <DialogPortal onClose={onClose}>
        <Root hasLogo={!!primaryLogoUrl}>
          <Icon logo={logoType} />
          <form onSubmit={handleSubmit}>
            <Instructions>
              Please enter your email address, birthdate, and ZIP Code to see if you have received an added-value bonus.
            </Instructions>
            <PhoneNumberFields
              disabled={isSending}
              onChange={data => {
                setPhoneData(data);
              }}
            />
            <FormField>
              <DOB>Birthdate*</DOB>
              <DateInput
                type="date"
                placeholder="mm/dd/yyyy"
                value={dateOfBirth}
                center
                min="1900-01-01"
                onChange={e => {
                  setDateOfBirth(e.target.value);
                  PlayFab.SetBirth(e.target.value);
                  setDateOfBirthChanged(true);
                }}
                isValid={
                  !showAgeError &&
                  (!dateOfBirth ||
                    !dateOfBirthChanged ||
                    dateOfBirthIsValid)
                }
              />
            </FormField>
            {showAgeError && <Error>Sorry, you are not eligible to participate</Error>}
            <ZipCodeField>
              <label>Zip Code*</label>
              <ZipCodeInput
                placeholder="12345"
                value={zipCode}
                disabled={isSending}
                onChange={(e: any) => {
                  setZipCode(e.target.rawValue);
                  setZipCodeChanged(true);
                }}
                isValid={(!zipCodeChanged || zipCodeIsValid) && zipCode !== "00000"}
              />
              </ZipCodeField>
            {/*<FormHint>Some Bonuses are only available in certain areas.</FormHint>*/}
            {error && <ErrorDisplay>{error}</ErrorDisplay>}
            {/*<FormHint>
              Your email address will only be used for sign in purposes.{" "}
            </FormHint>*/}
            <Opt htmlFor="acceptOptIn">
              <input
                id="acceptOptIn"
                type="checkbox"
                disabled={isSending}
                checked={acceptedOptIn}
                onChange={e => {
                  setAcceptOptIn(e.target.checked);
                }}
              />{" "}
              I would like to receive commercial emails from{" "}
              <a
                href="https://www.mlb.com/rays"
                target="_blank"
                rel="noopener noreferrer"
              >
                raysbaseball.com
              </a>
              ,{" "}
              <a
                href="https://www.mlb.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                mlb.com
              </a>
              , and their partners (optional).
            </Opt>
            <Terms htmlFor="acceptTandC">
              <input
                id="acceptTandC"
                type="checkbox"
                disabled={isSending}
                checked={acceptedTerms}
                onChange={e => {
                  setAcceptedTerms(e.target.checked);
                }}
              />{" "}
              I accept the{" "}
              <a
                href="https://www.mlb.com/official-information/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </a>
              , {" "}
              <a
                href="https://www.mlb.com/official-information/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              , and {" "}
              <a
                href="http://www.raysbaseball.com/meadowsrules"
                target="_blank"
                rel="noopener noreferrer"
              >
                Official Rules
              </a>
              *
            </Terms>
            <SubmitButton
              onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
              disabled={
                isSending || !acceptedTerms || !countryCode || !phoneNumber || !zipCodeIsValid
              }
            >
              Unlock My Bonus
            </SubmitButton>
          </form>
        </Root>
      </DialogPortal>
    );
  }
}
